import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Trash, Tag, X, MagnifyingGlass } from '@phosphor-icons/react';
import { getCoupons, createCoupon, updateCoupon, deleteCoupon } from '../../utils/api';
import { toast } from 'sonner';

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState(null);
  
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    description: '',
    min_order_amount: '',
    max_discount_amount: '',
    usage_limit: '',
    start_date: '',
    end_date: '',
    is_active: true
  });

  useEffect(() => {
    fetchCoupons();
  }, [search, filterActive]);

  const fetchCoupons = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterActive !== null) params.is_active = filterActive;
      
      const response = await getCoupons(params);
      if (response.data.success) {
        setCoupons(response.data.data || []);
      }
    } catch (error) {
      toast.error('Failed to fetch coupons');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      description: '',
      min_order_amount: '',
      max_discount_amount: '',
      usage_limit: '',
      start_date: '',
      end_date: '',
      is_active: true
    });
    setEditingCoupon(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value || '',
      description: coupon.description || '',
      min_order_amount: coupon.min_order_amount || '',
      max_discount_amount: coupon.max_discount_amount || '',
      usage_limit: coupon.usage_limit || '',
      start_date: coupon.start_date ? coupon.start_date.split('T')[0] : '',
      end_date: coupon.end_date ? coupon.end_date.split('T')[0] : '',
      is_active: coupon.is_active
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const params = {
      discount_type: formData.discount_type,
      discount_value: parseFloat(formData.discount_value),
      description: formData.description || undefined,
      min_order_amount: formData.min_order_amount ? parseFloat(formData.min_order_amount) : undefined,
      max_discount_amount: formData.max_discount_amount ? parseFloat(formData.max_discount_amount) : undefined,
      usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : undefined,
      start_date: formData.start_date || undefined,
      end_date: formData.end_date || undefined,
    };

    try {
      if (editingCoupon) {
        params.is_active = formData.is_active;
        await updateCoupon(editingCoupon.id, params);
        toast.success('Coupon updated successfully');
      } else {
        params.code = formData.code;
        await createCoupon(params);
        toast.success('Coupon created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save coupon');
    }
  };

  const handleDelete = async (coupon) => {
    if (!window.confirm(`Delete coupon "${coupon.code}"?`)) return;
    
    try {
      await deleteCoupon(coupon.id);
      toast.success('Coupon deleted');
      fetchCoupons();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  const toggleActive = async (coupon) => {
    try {
      await updateCoupon(coupon.id, { is_active: !coupon.is_active });
      toast.success(`Coupon ${coupon.is_active ? 'deactivated' : 'activated'}`);
      fetchCoupons();
    } catch (error) {
      toast.error('Failed to update coupon');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-display text-3xl text-secondary">Coupons</h1>
          <p className="font-body text-text-muted">Manage discount codes</p>
        </div>
        <button
          onClick={openCreateModal}
          data-testid="create-coupon-btn"
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={20} />
          Create Coupon
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            placeholder="Search coupons..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-border-default rounded focus:border-primary focus:outline-none font-body"
          />
        </div>
        <select
          value={filterActive === null ? '' : filterActive.toString()}
          onChange={(e) => setFilterActive(e.target.value === '' ? null : e.target.value === 'true')}
          className="px-4 py-2 border border-border-default rounded focus:border-primary focus:outline-none font-body"
        >
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      </div>

      {/* Coupons Table */}
      <div className="bg-white rounded-lg border border-border-default overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : coupons.length === 0 ? (
          <div className="p-8 text-center">
            <Tag size={48} className="text-text-muted mx-auto mb-4" />
            <p className="font-body text-text-muted">No coupons found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-background-subtle">
                <tr>
                  <th className="px-6 py-3 text-left font-body text-sm text-text-secondary">Code</th>
                  <th className="px-6 py-3 text-left font-body text-sm text-text-secondary">Discount</th>
                  <th className="px-6 py-3 text-left font-body text-sm text-text-secondary">Min Order</th>
                  <th className="px-6 py-3 text-left font-body text-sm text-text-secondary">Usage</th>
                  <th className="px-6 py-3 text-left font-body text-sm text-text-secondary">Valid Until</th>
                  <th className="px-6 py-3 text-left font-body text-sm text-text-secondary">Status</th>
                  <th className="px-6 py-3 text-right font-body text-sm text-text-secondary">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-default">
                {coupons.map((coupon) => (
                  <tr key={coupon.id} className="hover:bg-background-subtle transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <span className="font-body font-medium text-secondary">{coupon.code}</span>
                        {coupon.description && (
                          <p className="font-body text-xs text-text-muted mt-1">{coupon.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-body text-primary font-medium">
                        {coupon.discount_type === 'percentage' 
                          ? `${coupon.discount_value}%` 
                          : `₹${coupon.discount_value}`}
                      </span>
                      {coupon.max_discount_amount && coupon.discount_type === 'percentage' && (
                        <p className="font-body text-xs text-text-muted">Max: ₹{coupon.max_discount_amount}</p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-body text-sm">
                        {coupon.min_order_amount ? `₹${coupon.min_order_amount}` : '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-body text-sm">
                        {coupon.used_count || 0}{coupon.usage_limit ? `/${coupon.usage_limit}` : ''}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-body text-sm">
                        {coupon.end_date 
                          ? new Date(coupon.end_date).toLocaleDateString('en-IN') 
                          : 'No expiry'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => toggleActive(coupon)}
                        className={`px-3 py-1 rounded-full font-body text-xs ${
                          coupon.is_active 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {coupon.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openEditModal(coupon)}
                          className="p-2 text-text-muted hover:text-primary transition-colors"
                          title="Edit"
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(coupon)}
                          className="p-2 text-text-muted hover:text-red-500 transition-colors"
                          title="Delete"
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-display text-2xl text-secondary">
                  {editingCoupon ? 'Edit Coupon' : 'Create Coupon'}
                </h2>
                <button onClick={() => setShowModal(false)} className="text-text-muted hover:text-secondary">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Code */}
                <div>
                  <label className="block font-body text-sm text-text-secondary mb-1">
                    Coupon Code *
                  </label>
                  <input
                    type="text"
                    required
                    disabled={!!editingCoupon}
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="e.g., SAVE20"
                    className="w-full px-4 py-2 border border-border-default rounded focus:border-primary focus:outline-none font-body disabled:bg-gray-100"
                  />
                </div>

                {/* Discount Type & Value */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-body text-sm text-text-secondary mb-1">
                      Discount Type *
                    </label>
                    <select
                      value={formData.discount_type}
                      onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                      className="w-full px-4 py-2 border border-border-default rounded focus:border-primary focus:outline-none font-body"
                    >
                      <option value="percentage">Percentage</option>
                      <option value="fixed">Fixed Amount</option>
                    </select>
                  </div>
                  <div>
                    <label className="block font-body text-sm text-text-secondary mb-1">
                      Discount Value *
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      max={formData.discount_type === 'percentage' ? '100' : undefined}
                      step="0.01"
                      value={formData.discount_value}
                      onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                      placeholder={formData.discount_type === 'percentage' ? '10' : '100'}
                      className="w-full px-4 py-2 border border-border-default rounded focus:border-primary focus:outline-none font-body"
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block font-body text-sm text-text-secondary mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="e.g., 20% off for new customers"
                    className="w-full px-4 py-2 border border-border-default rounded focus:border-primary focus:outline-none font-body"
                  />
                </div>

                {/* Min Order & Max Discount */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-body text-sm text-text-secondary mb-1">
                      Min Order Amount
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.min_order_amount}
                      onChange={(e) => setFormData({ ...formData, min_order_amount: e.target.value })}
                      placeholder="0"
                      className="w-full px-4 py-2 border border-border-default rounded focus:border-primary focus:outline-none font-body"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-sm text-text-secondary mb-1">
                      Max Discount (₹)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.max_discount_amount}
                      onChange={(e) => setFormData({ ...formData, max_discount_amount: e.target.value })}
                      placeholder="No limit"
                      className="w-full px-4 py-2 border border-border-default rounded focus:border-primary focus:outline-none font-body"
                    />
                  </div>
                </div>

                {/* Usage Limit */}
                <div>
                  <label className="block font-body text-sm text-text-secondary mb-1">
                    Usage Limit
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.usage_limit}
                    onChange={(e) => setFormData({ ...formData, usage_limit: e.target.value })}
                    placeholder="Unlimited"
                    className="w-full px-4 py-2 border border-border-default rounded focus:border-primary focus:outline-none font-body"
                  />
                </div>

                {/* Date Range */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-body text-sm text-text-secondary mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      className="w-full px-4 py-2 border border-border-default rounded focus:border-primary focus:outline-none font-body"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-sm text-text-secondary mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="w-full px-4 py-2 border border-border-default rounded focus:border-primary focus:outline-none font-body"
                    />
                  </div>
                </div>

                {/* Active Toggle (only for edit) */}
                {editingCoupon && (
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4 accent-primary"
                    />
                    <label htmlFor="is_active" className="font-body text-sm text-text-secondary">
                      Active (customers can use this coupon)
                    </label>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-4 pt-4">
                  <button type="submit" className="btn-primary flex-1">
                    {editingCoupon ? 'Update Coupon' : 'Create Coupon'}
                  </button>
                  <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminCoupons;
